import { useQuery } from "@apollo/client";
import { Checkbox } from "@mui/material";
import { Dropdown } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "../../components/common/table/Table";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { listKeywordGroups } from "../../graphql/queries/listKeywordGroups";
import {
  MESSAGES,
  PRODUCT_HEADER_DATA,
  STYLES,
  TABLE_CONFIG,
} from "./productConstants";
import { productFilterSchema, productFilterUISchema } from "./productFormSchema";
import "./productManagement.scss";
import { transformProductsToRowData } from "./productUtils";

const ProductsTable = ({
  products,
  loading,
  filterData,
  productGroups = [],
  productGroupMap = {},
  handleView,
  handleEdit,
  handleSoftDelete,
  handleFilterChange,
  selectable = false,
  selectedProductIds = [],
  onSelectionChange,
}) => {
  const [selectedProductGroup, setSelectedProductGroup] = useState([]);

  // Fetch all OUTLET keyword groups in a single call and build id -> name map
  const { data: keywordGroupsData } = useQuery(listKeywordGroups, {
    variables: { type: "OUTLET" },
    fetchPolicy: "cache-and-network",
  });

  const keywordGroupMap = useMemo(() => {
    const map = {};
    (keywordGroupsData?.listKeywordGroups || []).forEach((kg) => {
      map[kg.id] = kg.name;
    });
    return map;
  }, [keywordGroupsData]);

  // Sync dropdown selection when filterData.productGroupId changes externally
  useEffect(() => {
    const groupId = filterData?.productGroupId;
    if (groupId) {
      const match = productGroups.find((g) => g.id === groupId);
      if (match) {
        setSelectedProductGroup([match]);
      }
    } else {
      setSelectedProductGroup([]);
    }
  }, [filterData?.productGroupId, productGroups]);

  // Handle product group dropdown change
  const handleProductGroupChange = useCallback(
    (selected) => {
      setSelectedProductGroup(selected);
      const groupId = selected?.[0]?.id || "";
      handleFilterChange({ ...filterData, productGroupId: groupId });
    },
    [handleFilterChange, filterData]
  );

  // Handle text filter changes (name, sku)
  const handleTextFilterChange = useCallback(
    (data) => {
      handleFilterChange({
        ...data,
        productGroupId: filterData.productGroupId || "",
      });
    },
    [handleFilterChange, filterData]
  );

  const rowData = transformProductsToRowData(
    products,
    keywordGroupMap,
    productGroupMap,
    handleView,
    handleEdit,
    handleSoftDelete
  );

  const allIds = useMemo(() => products.map((p) => p.id), [products]);
  const allSelected =
    selectable &&
    allIds.length > 0 &&
    allIds.every((id) => selectedProductIds.includes(id));
  const someSelected = selectable && selectedProductIds.length > 0 && !allSelected;

  const handleToggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...allIds]);
    }
  }, [allSelected, allIds, onSelectionChange]);

  const handleToggleRow = useCallback(
    (productId) => {
      if (!onSelectionChange) return;
      if (selectedProductIds.includes(productId)) {
        onSelectionChange(selectedProductIds.filter((id) => id !== productId));
      } else {
        onSelectionChange([...selectedProductIds, productId]);
      }
    },
    [selectedProductIds, onSelectionChange]
  );

  const headerData = useMemo(() => {
    if (!selectable) return PRODUCT_HEADER_DATA;

    const checkboxHeader = {
      columnKey: "checkbox",
      header: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleToggleAll}
          size="small"
          sx={{ padding: 0 }}
        />
      ),
      className: "product-checkbox-col",
    };
    return [checkboxHeader, ...PRODUCT_HEADER_DATA];
  }, [selectable, allSelected, someSelected, handleToggleAll]);

  const enhancedRowData = useMemo(() => {
    if (!selectable) return rowData;

    return rowData.map((row, index) => {
      const product = products[index];
      return {
        checkbox: (
          <Checkbox
            checked={selectedProductIds.includes(product?.id)}
            onChange={() => handleToggleRow(product?.id)}
            size="small"
            sx={{ padding: 0 }}
          />
        ),
        ...row,
      };
    });
  }, [selectable, rowData, products, selectedProductIds, handleToggleRow]);

  return (
    <div className="products-table-container">
      <div className="products-table-header">
        <div className="filter-wrapper">
          <JsonFormsWrapper
            jsonschema={productFilterSchema}
            uischema={productFilterUISchema}
            data={filterData}
            onChange={handleTextFilterChange}
          />
          <div className="product-group-filter">
            <Dropdown
              childList={productGroups}
              selection="single"
              selectedValue={selectedProductGroup}
              setSelectedValue={handleProductGroupChange}
              placeholder="Product Group"
              size="medium"
              isClearable
            />
          </div>
        </div>
      </div>
      {loading ? (
        <div style={STYLES.loadingContainer}>{MESSAGES.LOADING}</div>
      ) : (
        <Table
          headerData={headerData}
          rowData={enhancedRowData}
          className="products-table"
          pagination={true}
          rowsPerPage={TABLE_CONFIG.ROWS_PER_PAGE}
          noDataLabel={TABLE_CONFIG.NO_DATA_LABEL}
        />
      )}
    </div>
  );
};

export default ProductsTable;
